import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "salesive-api-axios";

// Utility to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Error categorization helper
const getErrorDetails = (error) => {
  const errorMessage = error.message || "Unknown error";
  const errorStr = errorMessage.toLowerCase();
  const status = error.response?.status;

  // Permission-related errors
  if (
    errorStr.includes("permission denied") ||
    errorStr.includes("denied") ||
    errorStr.includes("permission") ||
    errorMessage === "Notification permission denied."
  ) {
    return {
      type: "permission",
      title: "Permission Required",
      message:
        "Please enable notifications in your browser settings. Click 'Open Settings' to configure permissions.",
      userMessage:
        "Notifications are disabled. Please enable them to receive updates.",
      canRetry: true,
      actionable: true,
      actionText: "Open Settings",
      severity: "medium",
    };
  }

  // Browser compatibility errors
  if (
    errorStr.includes("service worker") ||
    errorStr.includes("not supported") ||
    errorStr.includes("unsupported") ||
    !("serviceWorker" in navigator)
  ) {
    return {
      type: "browser",
      title: "Browser Not Supported",
      message:
        "Your browser doesn't support push notifications. Please try with Chrome, Firefox, or Edge (latest versions).",
      userMessage: "Your browser doesn't support notifications.",
      canRetry: false,
      actionable: false,
      severity: "high",
    };
  }

  // Authentication errors
  if (
    status === 401 ||
    status === 403 ||
    errorStr.includes("authentication") ||
    errorStr.includes("unauthorized") ||
    errorStr.includes("token")
  ) {
    return {
      type: "auth",
      title: "Authentication Error",
      message:
        "Your session has expired. Please refresh the page and try again.",
      userMessage: "Session expired. Please refresh.",
      canRetry: true,
      actionable: true,
      actionText: "Refresh Page",
      severity: "medium",
    };
  }

  // Network errors
  if (
    error.code === "NETWORK_ERROR" ||
    !navigator.onLine ||
    errorStr.includes("network") ||
    errorStr.includes("offline") ||
    errorStr.includes("failed to fetch")
  ) {
    return {
      type: "network",
      title: "Connection Error",
      message:
        "Unable to connect to the server. Please check your internet connection and try again.",
      userMessage: "Network error. Check your connection.",
      canRetry: true,
      actionable: false,
      severity: "medium",
    };
  }

  // Configuration errors (VAPID key, server config)
  if (
    errorStr.includes("vapid") ||
    errorStr.includes("key") ||
    errorStr.includes("configuration") ||
    errorStr.includes("invalid")
  ) {
    return {
      type: "config",
      title: "Configuration Error",
      message:
        "Unable to configure notifications. Please try again later or contact support.",
      userMessage: "Configuration error. Please try again.",
      canRetry: true,
      actionable: false,
      severity: "medium",
    };
  }

  // Already subscribed
  if (
    status === 409 ||
    errorStr.includes("already") ||
    errorStr.includes("exists") ||
    errorStr.includes("duplicate")
  ) {
    return {
      type: "duplicate",
      title: "Already Subscribed",
      message: "You are already subscribed to notifications. No action needed.",
      userMessage: "Already subscribed to notifications.",
      canRetry: false,
      actionable: false,
      severity: "info",
    };
  }

  // Server errors
  if (
    status >= 500 ||
    errorStr.includes("server") ||
    errorStr.includes("internal")
  ) {
    return {
      type: "server",
      title: "Server Error",
      message:
        "Our notification service is temporarily unavailable. Please try again in a few minutes.",
      userMessage: "Server error. Please try again later.",
      canRetry: true,
      actionable: false,
      severity: "high",
    };
  }

  // Rate limiting
  if (
    status === 429 ||
    errorStr.includes("rate limit") ||
    errorStr.includes("too many")
  ) {
    return {
      type: "rate_limit",
      title: "Too Many Requests",
      message: "Please wait a moment before trying again.",
      userMessage: "Please wait before trying again.",
      canRetry: true,
      actionable: false,
      severity: "low",
    };
  }

  // Default generic error
  return {
    type: "generic",
    title: "Error",
    message: errorMessage,
    userMessage: "Something went wrong. Please try again.",
    canRetry: true,
    actionable: false,
    severity: "medium",
  };
};

export const PushNotificationModal = forwardRef(({ darkMode = false }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [subscriptionCount, setSubscriptionCount] = useState(0);

  // State for notification subscription process
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [vapidKey, setVapidKey] = useState("");
  const [activeSubscription, setActiveSubscription] = useState(null);

  // Refs for cleanup
  const errorTimerRef = useRef(null);
  const successTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setIsVisible(true);
    },
    getSubscriptionCount: () => subscriptionCount,
    closeModal: () => {
      setIsVisible(false);
    },
  }));

  useEffect(() => {
    const checkSubsAndSchedule = async () => {
      // Check for permanent acceptance
      const alreadyAccepted =
        localStorage.getItem("salesive_push_interaction") === "accepted";
      if (alreadyAccepted || hasInteracted) return;

      // Check if declined recently (within 24 hours)
      const declinedAt = localStorage.getItem("salesive_push_declined_at");
      if (declinedAt) {
        const oneDay = 24 * 60 * 60 * 1000;
        const timeSinceDecline = Date.now() - Number(declinedAt);
        if (timeSinceDecline < oneDay) {
          return;
        }
      }

      try {
        // Check if user already has subscriptions
        const response = await api.get("/push-subscriptions");
        const subs = response?.data?.data?.subscriptions || [];
        setSubscriptionCount(subs.length);

        if (subs && subs.length > 0) {
          // User already has subscriptions, mark as accepted implicitly
          localStorage.setItem("salesive_push_interaction", "accepted");
          return;
        }

        // Show after 2 minutes if no subs and valid to ask
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 120000);
        return () => clearTimeout(timer);
      } catch (err) {
        console.error("[SalesiveWidget] Failed to check subscriptions", err);
        // Don't show modal if we can't check subscriptions
      }
    };

    checkSubsAndSchedule();
  }, [hasInteracted]);

  // Check for existing subscription when modal becomes visible
  useEffect(() => {
    if (isVisible && status === "idle") {
      const checkExisting = async () => {
        try {
          const response = await api.get("/push-subscriptions");
          const subs = response?.data?.data?.subscriptions || [];
          if (subs.length > 0) {
            setSubscriptionCount(subs.length);
            // User already has subscriptions, close modal
            setIsVisible(false);
            localStorage.setItem("salesive_push_interaction", "accepted");
          }
        } catch (err) {
          // Silent fail - we'll still allow subscription attempt
          console.error(
            "[SalesiveWidget] Failed to check existing subscriptions:",
            err,
          );
        }
      };

      checkExisting();
    }
  }, [isVisible, status]);

  // Fetch VAPID key with retry logic
  const fetchVapidKey = async (retryCount = 0) => {
    try {
      const res = await api.get("/push-subscriptions/public-key");
      if (res.data?.data?.publicKey) {
        setVapidKey(res.data.data.publicKey);
        return res.data.data.publicKey;
      } else {
        throw new Error("Invalid VAPID key response");
      }
    } catch (error) {
      console.error("[SalesiveWidget] Failed to fetch VAPID key:", error);

      // Retry logic for network errors
      if (
        retryCount < 2 &&
        (!error.response || error.response?.status >= 500)
      ) {
        console.log(
          `[SalesiveWidget] Retrying VAPID key fetch (${retryCount + 1}/2)`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * (retryCount + 1)),
        );
        return fetchVapidKey(retryCount + 1);
      }

      const errorDetails = getErrorDetails(error);
      throw new Error(errorDetails.userMessage);
    }
  };

  // Handle cleanup of failed subscription
  const cleanupFailedSubscription = async (subscription) => {
    if (!subscription) return;

    try {
      const isSubscribed = await subscription.unsubscribe();
      console.log(
        "[SalesiveWidget] Cleaned up failed subscription:",
        isSubscribed,
      );
    } catch (cleanupError) {
      console.error(
        "[SalesiveWidget] Failed to cleanup subscription:",
        cleanupError,
      );
    }
  };

  // Handle specific error actions
  const handleErrorAction = async (errorType) => {
    switch (errorType) {
      case "permission":
        // Request permission again
        try {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            // Retry subscription if permission granted
            await handleAccept();
          }
        } catch (error) {
          console.error(
            "[SalesiveWidget] Failed to request permission:",
            error,
          );
        }
        break;

      case "auth":
        // Refresh page for auth errors
        window.location.reload();
        break;

      default:
        // For other error types, just clear error state
        setStatus("idle");
        setErrorDetails(null);
        setMessage("");
        break;
    }
  };

  const handleAccept = async () => {
    // Clear any existing timers
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }

    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }

    // Reset state if retrying from error
    if (status === "error") {
      setStatus("idle");
      setErrorDetails(null);
      setMessage("");
    }

    setStatus("loading");
    setMessage("Requesting permission...");

    try {
      // Fetch VAPID key if we don't have it
      let key = vapidKey;
      if (!key) {
        key = await fetchVapidKey();
      }

      if (!key) {
        throw new Error("Unable to retrieve notification configuration.");
      }

      // 1. Request Permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied.");
      }

      setMessage("Preparing subscription...");

      // 2. Register Service Worker & get registration
      if (!("serviceWorker" in navigator)) {
        throw new Error("Service worker not supported.");
      }

      // Register SW if not already registered (file served by host)
      let registration = await navigator.serviceWorker.getRegistration("/");
      if (!registration) {
        registration = await navigator.serviceWorker.register("/sw.js");
      }

      // Wait for the SW to be ready
      if (!registration.active) {
        await new Promise((resolve, reject) => {
          const sw = registration.installing || registration.waiting;
          if (!sw) { reject(new Error("Service worker failed to install.")); return; }
          const onStateChange = () => {
            if (sw.state === "activated") { sw.removeEventListener("statechange", onStateChange); resolve(); }
            if (sw.state === "redundant") { sw.removeEventListener("statechange", onStateChange); reject(new Error("Service worker became redundant.")); }
          };
          sw.addEventListener("statechange", onStateChange);
          setTimeout(() => reject(new Error("Service worker activation timed out.")), 10000);
        });
      }

      setMessage("Subscribing to Push...");

      // 3. Subscribe to Push
      let convertedKey;
      try {
        convertedKey = urlBase64ToUint8Array(key);
      } catch (e) {
        throw new Error("Invalid notification configuration.");
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
      });

      // Store subscription for potential cleanup
      setActiveSubscription(subscription);

      setMessage("Saving subscription...");

      // 4. Send to Server
      const payload = {
        subscription: subscription,
        label: navigator.userAgent || "Web Client",
      };

      try {
        await api.post("/push-subscriptions", payload);
      } catch (error) {
        console.error("[SalesiveWidget] Failed to save subscription:", error);

        // Clean up the subscription we just created
        await cleanupFailedSubscription(subscription);
        setActiveSubscription(null);

        throw error; // Re-throw for error handling
      }

      // Clear active subscription on success
      setActiveSubscription(null);

      // Update subscription count and set success state
      setSubscriptionCount((prev) => prev + 1);
      setStatus("success");
      setMessage(
        "Successfully subscribed! You'll now receive real-time updates.",
      );

      // Mark as accepted in local storage
      setHasInteracted(true);
      localStorage.setItem("salesive_push_interaction", "accepted");
      localStorage.removeItem("salesive_push_declined_at");

      // Close modal after success (after showing success message)
      successTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        setStatus("idle");
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error("[SalesiveWidget] Notification subscription error:", error);

      // Categorize the error
      const details = getErrorDetails(error);
      setErrorDetails(details);
      setStatus("error");
      setMessage(details.userMessage);

      // Don't mark as interacted on error, to allow retry
      // Clear error state after some time for non-fatal errors
      if (details.canRetry && details.severity !== "high") {
        errorTimerRef.current = setTimeout(() => {
          if (status === "error") {
            setStatus("idle");
            setErrorDetails(null);
            setMessage("");
          }
        }, 8000);
      }
    }
  };

  const handleDecline = () => {
    // Clean up any active subscription
    if (activeSubscription) {
      cleanupFailedSubscription(activeSubscription);
      setActiveSubscription(null);
    }

    // Clear timers
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);

    setIsVisible(false);
    setHasInteracted(true);
    setStatus("idle");
    setErrorDetails(null);
    setMessage("");
    localStorage.setItem("salesive_push_declined_at", Date.now().toString());
  };

  const handleRetry = () => {
    if (errorDetails?.canRetry) {
      handleAccept();
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        when: "afterChildren",
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.5 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 25 },
    },
    exit: { opacity: 0, scale: 0.9, y: 20 },
  };

  // Get button text based on state
  const getButtonText = () => {
    if (status === "loading") return "Processing...";
    if (status === "success") return "Subscribed";
    if (status === "error") {
      if (errorDetails?.canRetry === false) return "Unavailable";
      return "Try Again";
    }
    return "Enable";
  };

  // Get button styling based on state
  const getButtonClass = () => {
    const baseClass =
      "tw-flex-1 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors tw-text-center";

    if (status === "loading") {
      return `${baseClass} tw-bg-primary/80 tw-text-white tw-cursor-wait`;
    }

    if (status === "success") {
      return `${baseClass} tw-bg-green-600 tw-text-white`;
    }

    if (status === "error") {
      if (errorDetails?.canRetry === false) {
        return `${baseClass} tw-bg-gray-500 tw-text-white tw-cursor-not-allowed`;
      }
      return `${baseClass} tw-bg-orange-600 tw-text-white hover:tw-bg-orange-700`;
    }

    return `${baseClass} tw-bg-primary tw-text-white hover:tw-bg-primary/90`;
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="tw-fixed tw-inset-0 tw-z-[2147483648] tw-flex tw-items-center tw-justify-center tw-pointer-events-none"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
        >
          {/* Backdrop */}
          <motion.div
            className="tw-absolute tw-inset-0 tw-bg-black tw-pointer-events-auto"
            onClick={handleDecline}
            variants={backdropVariants}
          />

          {/* Modal */}
          <motion.div
            className={`
              tw-relative tw-w-[85%] tw-max-w-sm tw-p-5 tw-rounded-2xl tw-shadow-xl tw-pointer-events-auto
              ${darkMode ? "tw-bg-gray-800 tw-text-white" : "tw-bg-white tw-text-gray-900"}
            `}
            variants={modalVariants}
          >
            {/* Icon & Title */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
              <div
                className={`tw-w-14 tw-h-14 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-md tw-flex-shrink-0 ${
                  status === "error" && errorDetails?.severity === "high"
                    ? "tw-bg-red-600"
                    : status === "error"
                      ? "tw-bg-orange-600"
                      : status === "success"
                        ? "tw-bg-green-600"
                        : darkMode
                          ? "tw-bg-primary"
                          : "tw-bg-primary"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="tw-w-8 tw-h-8 tw-text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {status === "error" ? (
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </>
                  ) : status === "success" ? (
                    <>
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </>
                  ) : (
                    <>
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </>
                  )}
                </svg>
              </div>
              <div className="tw-flex-1">
                <h3 className="tw-text-base tw-font-semibold tw-mb-0.5">
                  {status === "error"
                    ? errorDetails?.title || "Error"
                    : status === "success"
                      ? "Notifications Enabled"
                      : "Enable Notifications"}
                </h3>
                <p
                  className={`tw-text-xs ${
                    darkMode ? "tw-text-gray-400" : "tw-text-gray-500"
                  }`}
                >
                  {status === "success"
                    ? "You're all set!"
                    : "Get instant updates"}
                </p>
              </div>
            </div>

            {/* Description */}
            {status !== "error" && status !== "success" && (
              <p
                className={`tw-text-sm tw-mb-4 ${
                  darkMode ? "tw-text-gray-400" : "tw-text-gray-500"
                }`}
              >
                Stay updated with support responses and order status in
                real-time.
              </p>
            )}

            {/* Status Message */}
            {message && (
              <div
                className={`tw-text-sm tw-mb-4 tw-p-3 tw-rounded-lg ${
                  status === "error"
                    ? darkMode
                      ? errorDetails?.severity === "high"
                        ? "tw-bg-red-900/30 tw-text-red-300 tw-border tw-border-red-700/50"
                        : "tw-bg-orange-900/30 tw-text-orange-300 tw-border tw-border-orange-700/50"
                      : errorDetails?.severity === "high"
                        ? "tw-bg-red-50 tw-text-red-800 tw-border tw-border-red-200"
                        : "tw-bg-orange-50 tw-text-orange-800 tw-border tw-border-orange-200"
                    : status === "success"
                      ? darkMode
                        ? "tw-bg-green-900/30 tw-text-green-300 tw-border tw-border-green-700/50"
                        : "tw-bg-green-50 tw-text-green-800 tw-border tw-border-green-200"
                      : darkMode
                        ? "tw-bg-primary/20 tw-text-primary"
                        : "tw-bg-primary/10 tw-text-primary"
                }`}
              >
                <div className="tw-font-medium tw-mb-1">
                  {status === "error"
                    ? errorDetails?.title || "Error"
                    : status === "success"
                      ? "Success!"
                      : "Processing..."}
                </div>
                <div className="tw-text-xs tw-opacity-90">{message}</div>
                {status === "error" && errorDetails?.actionable && (
                  <button
                    onClick={() => handleErrorAction(errorDetails.type)}
                    className={`tw-mt-2 tw-text-xs tw-font-medium tw-py-1.5 tw-px-3 tw-rounded tw-transition-colors ${
                      darkMode
                        ? errorDetails?.severity === "high"
                          ? "tw-bg-red-700 hover:tw-bg-red-600 tw-text-white"
                          : "tw-bg-orange-700 hover:tw-bg-orange-600 tw-text-white"
                        : errorDetails?.severity === "high"
                          ? "tw-bg-red-100 hover:tw-bg-red-200 tw-text-red-800"
                          : "tw-bg-orange-100 hover:tw-bg-orange-200 tw-text-orange-800"
                    }`}
                  >
                    {errorDetails.actionText}
                  </button>
                )}
                {status === "error" && errorDetails?.type === "duplicate" && (
                  <div className="tw-mt-2 tw-text-xs tw-opacity-75">
                    You can manage your subscriptions in your account settings.
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div className="tw-flex tw-gap-2">
              <span
                onClick={handleDecline}
                className={`
                  tw-flex-1 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors tw-text-center tw-cursor-pointer
                  ${
                    darkMode
                      ? "tw-text-gray-400 hover:tw-bg-gray-700"
                      : "tw-text-gray-500 hover:tw-bg-gray-100"
                  }
                `}
              >
                {status === "error" && errorDetails?.canRetry === false
                  ? "Close"
                  : "Not now"}
              </span>
              <span
                onClick={
                  status === "error" && errorDetails?.canRetry
                    ? handleRetry
                    : handleAccept
                }
                className={getButtonClass()}
                disabled={
                  status === "loading" ||
                  (status === "error" && errorDetails?.canRetry === false)
                }
              >
                {status === "loading" ? (
                  <>
                    <span className="tw-inline-block tw-w-4 tw-h-4 tw-mr-2 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin"></span>
                    Processing...
                  </>
                ) : status === "success" ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="tw-inline-block tw-w-4 tw-h-4 tw-mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Subscribed
                  </>
                ) : (
                  getButtonText()
                )}
              </span>
            </div>

            {/* Debug info (only in development) */}
            {process.env.NODE_ENV === "development" &&
              status === "error" &&
              errorDetails && (
                <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
                  <details className="tw-text-xs">
                    <summary className="tw-cursor-pointer tw-opacity-50">
                      Debug Info
                    </summary>
                    <pre className="tw-mt-1 tw-p-2 tw-bg-gray-100 dark:tw-bg-gray-900 tw-rounded tw-overflow-auto tw-max-h-32">
                      {JSON.stringify(errorDetails, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
